'use client';

import { useCallback, useMemo, useState } from 'react';
import {
  Bell,
  CheckCheck,
  Heart,
  MessageCircle,
  MessageSquare,
  Send,
  Star,
  User,
  UserPlus,
  XIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Avatar, AvatarFallback, AvatarImage } from '@kit/ui/avatar';
import { Button } from '@kit/ui/button';
import { If } from '@kit/ui/if';
import { Popover, PopoverContent, PopoverTrigger } from '@kit/ui/popover';
import { Separator } from '@kit/ui/separator';
import { Skeleton } from '@kit/ui/skeleton';
import { cn } from '@kit/ui/utils';

import {
  useCommunityNotifications,
  useCommunityUnreadCount,
  useDismissCommunityNotification,
  useMarkAllCommunityNotificationsAsRead,
  useMarkCommunityNotificationsAsRead,
  type CommunityNotificationWithRecipient,
} from '../hooks/use-community-notifications';

interface CommunityNotificationsPopoverProps {
  profileId?: string;
  organizationId?: string;
  realtime?: boolean;
  onNotificationClick?: (
    notification: CommunityNotificationWithRecipient,
  ) => void;
}

export function CommunityNotificationsPopover({
  profileId,
  organizationId,
  realtime = true,
  onNotificationClick,
}: CommunityNotificationsPopoverProps) {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);

  const {
    data: notifications,
    isLoading,
    error,
  } = useCommunityNotifications({
    profileId,
    organizationId,
    realtime,
  });

  const { data: unreadCount = 0 } = useCommunityUnreadCount({
    profileId,
    organizationId,
  });

  const markAsRead = useMarkCommunityNotificationsAsRead();
  const markAllAsRead = useMarkAllCommunityNotificationsAsRead();
  const dismissNotification = useDismissCommunityNotification();

  // Mark notifications as read when popover opens
  const handleOpenChange = useCallback(
    async (isOpen: boolean) => {
      setOpen(isOpen);

      if (isOpen && notifications?.length) {
        const unreadIds = notifications
          .filter((n) => !n.recipient?.read_at)
          .map((n) => n.id);

        if (unreadIds.length > 0) {
          await markAsRead(unreadIds);
        }
      }
    },
    [notifications, markAsRead],
  );

  const handleMarkAllAsRead = useCallback(async () => {
    await markAllAsRead({ profileId, organizationId });
  }, [markAllAsRead, profileId, organizationId]);

  const handleDismiss = useCallback(
    async (notificationId: string, e: React.MouseEvent) => {
      e.stopPropagation();
      await dismissNotification(notificationId);
    },
    [dismissNotification],
  );

  const handleNotificationClick = useCallback(
    (notification: CommunityNotificationWithRecipient) => {
      if (notification.link) {
        window.location.href = notification.link;
      }
      onNotificationClick?.(notification);
    },
    [onNotificationClick],
  );

  // Format relative time
  const timeAgo = useCallback(
    (createdAt: string) => {
      const date = new Date(createdAt);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / (1000 * 60));
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

      const formatter = new Intl.RelativeTimeFormat(i18n.language, {
        numeric: 'auto',
      });

      if (diffMins < 5) {
        return t('common:justNow');
      }
      if (diffMins < 60) {
        return formatter.format(-diffMins, 'minute');
      }
      if (diffHours < 24) {
        return formatter.format(-diffHours, 'hour');
      }
      if (diffDays < 30) {
        return formatter.format(-diffDays, 'day');
      }
      if (diffDays < 365) {
        return formatter.format(-Math.floor(diffDays / 30), 'month');
      }
      return formatter.format(-Math.floor(diffDays / 365), 'year');
    },
    [i18n.language, t],
  );

  return (
    <Popover modal open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          className="relative h-9 w-9"
          variant="ghost"
          aria-label={t('common:notifications', 'Notifications')}
        >
          <Bell className="min-h-4 min-w-4" aria-hidden="true" />

          <span
            className={cn(
              'animate-in fade-in zoom-in bg-destructive text-destructive-foreground absolute top-1 right-1 flex h-4 w-4 items-center justify-center rounded-full text-[0.6rem] font-medium',
              {
                hidden: unreadCount === 0,
              },
            )}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        </Button>
      </PopoverTrigger>

      <PopoverContent
        className="flex w-full max-w-md flex-col p-0 lg:min-w-80"
        align="end"
        collisionPadding={20}
        sideOffset={10}
      >
        <div className="flex items-center justify-between px-4 py-3">
          <span className="text-sm font-semibold">
            {t('common:notifications', 'Notifications')}
          </span>

          <If condition={unreadCount > 0}>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={handleMarkAllAsRead}
            >
              <CheckCheck className="mr-1 h-3 w-3" />
              {t('common:markAllAsRead', 'Mark all as read')}
            </Button>
          </If>
        </div>

        <Separator />

        <div className="max-h-[60vh] overflow-y-auto">
          <If condition={isLoading}>
            <div className="space-y-3 p-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex gap-3">
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-3 w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          </If>

          <If condition={error}>
            <div className="text-muted-foreground p-4 text-center text-sm">
              {t(
                'common:errorLoadingNotifications',
                'Error loading notifications',
              )}
            </div>
          </If>

          <If condition={!isLoading && !error && notifications?.length === 0}>
            <div className="text-muted-foreground p-8 text-center text-sm">
              {t('common:noNotifications', 'No notifications yet')}
            </div>
          </If>

          <If
            condition={
              !isLoading && !error && notifications && notifications.length > 0
            }
          >
            <div className="divide-y">
              {notifications?.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  timeAgo={timeAgo}
                  onClick={() => handleNotificationClick(notification)}
                  onDismiss={(e) => handleDismiss(notification.id, e)}
                />
              ))}
            </div>
          </If>
        </div>
      </PopoverContent>
    </Popover>
  );
}

interface NotificationItemProps {
  notification: CommunityNotificationWithRecipient;
  timeAgo: (createdAt: string) => string;
  onClick: () => void;
  onDismiss: (e: React.MouseEvent) => void;
}

function NotificationItem({
  notification,
  timeAgo,
  onClick,
  onDismiss,
}: NotificationItemProps) {
  const isRead = !!notification.recipient?.read_at;

  const NotificationIcon = useMemo(() => {
    switch (notification.notification_type) {
      case 'like_post':
      case 'like_comment':
        return Heart;
      case 'comment_post':
      case 'reply_comment':
        return MessageCircle;
      case 'new_follower':
      case 'follow_request':
      case 'follow_accepted':
        return UserPlus;
      case 'new_message':
      case 'message_request':
        return MessageSquare;
      case 'offer_received':
      case 'offer_accepted':
        return Send;
      case 'profile_featured':
        return Star;
      case 'profile_viewed':
        return User;
      default:
        return Bell;
    }
  }, [notification.notification_type]);

  const iconColor = useMemo(() => {
    switch (notification.notification_type) {
      case 'like_post':
      case 'like_comment':
        return 'text-red-500';
      case 'offer_accepted':
      case 'follow_accepted':
        return 'text-green-500';
      case 'offer_received':
        return 'text-blue-500';
      case 'profile_featured':
        return 'text-yellow-500';
      default:
        return 'text-muted-foreground';
    }
  }, [notification.notification_type]);

  return (
    <div
      className={cn(
        'hover:bg-muted/50 flex cursor-pointer items-start gap-3 px-4 py-3 transition-colors',
        {
          'bg-info/5': !isRead,
        },
      )}
      onClick={onClick}
    >
      <div className="relative">
        <Avatar className="h-10 w-10">
          <AvatarImage src={undefined} alt="" />
          <AvatarFallback>
            <NotificationIcon className={cn('h-5 w-5', iconColor)} />
          </AvatarFallback>
        </Avatar>

        <If condition={!isRead}>
          <span className="bg-info absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full" />
        </If>
      </div>

      <div className="min-w-0 flex-1">
        <If condition={notification.title}>
          <p className="truncate text-sm font-medium">{notification.title}</p>
        </If>

        <If condition={notification.body}>
          <p
            className={cn('text-sm', {
              'text-muted-foreground': isRead,
            })}
          >
            {notification.body}
          </p>
        </If>

        <p className="text-muted-foreground mt-1 text-xs">
          {timeAgo(notification.created_at)}
        </p>
      </div>

      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7 shrink-0 opacity-0 transition-opacity group-hover:opacity-100 hover:opacity-100"
        onClick={onDismiss}
      >
        <XIcon className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}
